/**
 * Interface extending HTMLElement to include custom properties for event listeners and range.
 */
interface ExtendedHTMLElement extends HTMLElement {
  inputListener?: EventListenerOrEventListenerObject;
  keydownListener?: EventListenerOrEventListenerObject;
  clickListener?: EventListenerOrEventListenerObject;
  pasteListener?: EventListenerOrEventListenerObject;
  selectionChangeListener?: EventListenerOrEventListenerObject;
  range?: Range;
}

/**
 * Class representing an HTML editor with various functionalities.
 */
export default class Editor {
  /**
   * Initializes the HTML editor with event listeners and configurations.
   * @param ref - The HTML element reference.
   * @param uploadUrl - The URL for uploading files.
   * @param paste - Boolean indicating if paste functionality is enabled.
   * @param instance - The Blazor instance for invoking methods.
   * @param shortcuts - Array of keyboard shortcuts.
   */
  createEditor(ref: ExtendedHTMLElement, uploadUrl: string, paste: boolean, instance: any, shortcuts: string[]) {
    console.log('createEditor', ref, uploadUrl, paste, instance, shortcuts);
    ref.inputListener = function () {
      console.log('inputListener', ref.innerHTML);
      instance.invokeMethodAsync('OnChange', ref.innerHTML);
    };
    ref.keydownListener = function (e: KeyboardEvent) {
      console.log('keydownListener', e);
      let key = '';
      if (e.ctrlKey || e.metaKey) {
        key += 'Ctrl+';
      }
      if (e.altKey) {
        key += 'Alt+';
      }
      if (e.shiftKey) {
        key += 'Shift+';
      }
      key += e.code.replace('Key', '').replace('Digit', '').replace('Numpad', '');

      if (shortcuts.includes(key)) {
        e.preventDefault();
        instance.invokeMethodAsync('ExecuteShortcutAsync', key);
      }
    };

    ref.clickListener = function (e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target) {
        if (target.matches('a,button')) {
          e.preventDefault();
        }

        for (const img of ref.querySelectorAll('img.fe-state-selected')) {
          img.classList.remove('fe-state-selected');
        }

        if (target.matches('img')) {
          target.classList.add('fe-state-selected');
          const range = document.createRange();
          range.selectNode(target);
          const selection = getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    };

    ref.selectionChangeListener = function () {
      if (document.activeElement === ref) {
        instance.invokeMethodAsync('OnSelectionChange');
      }
    };
    ref.pasteListener = function (e: ClipboardEvent) {
      const item = e.clipboardData?.items[0];

      if (item && item.kind === 'file') {
        e.preventDefault();
        const file = item.getAsFile();

        if (uploadUrl && file) {
          const xhr = new XMLHttpRequest();
          const data = new FormData();
          data.append("file", file);
          xhr.onreadystatechange = function () {
            if (xhr.readyState === XMLHttpRequest.DONE) {
              const status = xhr.status;
              if (status === 0 || (status >= 200 && status < 400)) {
                const result = JSON.parse(xhr.responseText);
                const html = '<img src="' + result.url + '">';
                if (paste) {
                  instance.invokeMethodAsync('OnPaste', html)
                    .then(function (html: string) {
                      document.execCommand("insertHTML", false, html);
                    });
                } else {
                  document.execCommand("insertHTML", false, '<img src="' + result.url + '">');
                }
                instance.invokeMethodAsync('OnUploadComplete', xhr.responseText);
              } else {
                instance.invokeMethodAsync('OnError', xhr.responseText);
              }
            }
          };
          instance.invokeMethodAsync('GetHeaders').then(function (headers: any) {
            xhr.open('POST', uploadUrl, true);
            for (const name in headers) {
              xhr.setRequestHeader(name, headers[name]);
            }
            xhr.send(data);
          });
        } else if (file) {
          const reader = new FileReader();
          reader.onload = function (e) {
            const html = '<img src="' + e.target?.result + '">';

            if (paste) {
              instance.invokeMethodAsync('OnPaste', html)
                .then(function (html: string) {
                  document.execCommand("insertHTML", false, html);
                });
            } else {
              document.execCommand("insertHTML", false, html);
            }
          };
          reader.readAsDataURL(file);
        }
      } else if (paste) {
        e.preventDefault();
        const data = e.clipboardData?.getData('text/html') || e.clipboardData?.getData('text/plain');

        instance.invokeMethodAsync('OnPaste', data)
          .then(function (html: string) {
            document.execCommand("insertHTML", false, html);
          });
      }
    };
    ref.addEventListener('input', ref.inputListener);
    ref.addEventListener('paste', ref.pasteListener);
    ref.addEventListener('keydown', ref.keydownListener);
    ref.addEventListener('click', ref.clickListener);
    document.addEventListener('selectionchange', ref.selectionChangeListener);
    document.execCommand('styleWithCSS', false, 'true');
  }

  /**
   * Saves the current selection range in the editor.
   * @param ref - The HTML element reference.
   */
  saveSelection(ref: ExtendedHTMLElement) {
    if (document.activeElement === ref) {
      const selection = getSelection();
      if (selection && selection.rangeCount > 0) {
        ref.range = selection.getRangeAt(0);
      }
    }
  }

  /**
   * Restores the previously saved selection range in the editor.
   * @param ref - The HTML element reference.
   */
  restoreSelection(ref: ExtendedHTMLElement) {
    const range = ref.range;
    if (range) {
      delete ref.range;
      if (ref) {
        ref.focus();
      }
      const selection = getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  /**
   * Retrieves the attributes of the selected element within the editor.
   * @param selector - The CSS selector to match the target element.
   * @param attributes - The list of attributes to retrieve.
   * @param container - The container element of the editor.
   * @returns An object containing the selected attributes.
   */
  selectionAttributes(selector: string, attributes: string[], container: HTMLElement) {
    console.log('selectionAttributes', selector, attributes, container);
    const selection = getSelection();
    const range = selection && selection.rangeCount > 0 && selection.getRangeAt(0);
    let parent = range && range.commonAncestorContainer;
    const img = container.querySelector('img.fe-state-selected');
    let inside = img && selector === 'img';
    while (parent) {
      if (parent === container) {
        inside = true;
        break;
      }
      parent = parent.parentNode as Node;
    }

    if (!inside) {
      return {};
    }

    let target = selection?.focusNode as HTMLElement;
    let innerHTML;

    if (img && selector === 'img') {
      target = img as HTMLElement;
    } else if (target) {
      if (target.nodeType === 3) {
        target = target.parentElement as HTMLElement;
      } else {
        target = target.childNodes[selection.focusOffset] as HTMLElement;
        if (target) {
          innerHTML = target.outerHTML;
        }
      }
      if (target && target.matches && !target.matches(selector)) {
        target = target.closest(selector) as HTMLElement;
      }
    }

    return attributes.reduce(function (result: any, name: string) {
      if (target) {
        result[name] = name === 'innerText' ? target[name] : target.getAttribute(name);
      }
      return result;
    }, { innerText: selection?.toString(), innerHTML: innerHTML });
  }

  /**
   * Destroys the HTML editor by removing event listeners.
   * @param ref - The HTML element reference.
   */
  destroyEditor(ref: ExtendedHTMLElement) {
    if (ref) {
      ref.removeEventListener('input', ref.inputListener);
      ref.removeEventListener('paste', ref.pasteListener);
      ref.removeEventListener('keydown', ref.keydownListener);
      ref.removeEventListener('click', ref.clickListener);
      document.removeEventListener('selectionchange', ref.selectionChangeListener);
    }
  }

  /**
   * Sets or gets the innerHTML of the reference element.
   * @param ref - The HTML element reference.
   * @param value - The value to set as innerHTML.
   * @returns The innerHTML if no value is provided.
   */
  innerHTML(ref: ExtendedHTMLElement, value?: string): string | void {
    console.log('innerHTML', ref, value);
    if (value !== undefined) {
      if (ref != null) {
        ref.innerHTML = value;
      }
    } else {
      return ref.innerHTML;
    }
  }

  /**
   * Executes a command on the reference element.
   * @param ref - The HTML element reference.
   * @param name - The command name.
   * @param value - The command value.
   * @returns The result of queryCommands.
   */
  execCommand(ref: ExtendedHTMLElement, name: string, value: string): any {
    console.log('execCommand', name, value);
    if (document.activeElement != ref && ref) {
      ref.focus();
    }
    document.execCommand(name, false, value);
    return this.queryCommands(ref);
  }

  /**
   * Queries the commands state of the reference element.
   * @param ref - The HTML element reference.
   * @returns An object containing the command states.
   */
  queryCommands(ref: ExtendedHTMLElement): any {
    return {
      html: ref != null ? ref.innerHTML : null,
      fontName: document.queryCommandValue('fontName'),
      fontSize: document.queryCommandValue('fontSize'),
      formatBlock: document.queryCommandValue('formatBlock'),
      bold: document.queryCommandState('bold'),
      underline: document.queryCommandState('underline'),
      justifyRight: document.queryCommandState('justifyRight'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyFull: document.queryCommandState('justifyFull'),
      italic: document.queryCommandState('italic'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      superscript: document.queryCommandState('superscript'),
      subscript: document.queryCommandState('subscript'),
      unlink: document.queryCommandEnabled('unlink'),
      undo: document.queryCommandEnabled('undo'),
      redo: document.queryCommandEnabled('redo')
    };
  }
}
